import { useCallback } from 'react';
import { logger } from '../../utils/logger';
import { playSpeakerTestTone } from './speaker-test.util';

interface UseMicrophoneSettingsHandlersOptions {
  isTestingMic: boolean;
  startMicTest: () => Promise<void>;
  stopMicTest: () => void;
  setSelectedInput: (deviceId: string) => void;
  saveDevicePreferences: () => void;
  onClose: () => void;
}

export function useMicrophoneSettingsHandlers({
  isTestingMic,
  startMicTest,
  stopMicTest,
  setSelectedInput,
  saveDevicePreferences,
  onClose,
}: UseMicrophoneSettingsHandlersOptions) {
  const handleSave = useCallback(() => {
    saveDevicePreferences();
    stopMicTest();
    onClose();
  }, [onClose, saveDevicePreferences, stopMicTest]);

  const handleMicTestToggle = useCallback(async () => {
    if (isTestingMic) {
      stopMicTest();
      return;
    }

    try {
      await startMicTest();
    } catch (error) {
      logger.error('[MicSettings] Error starting mic test:', error);
    }
  }, [isTestingMic, startMicTest, stopMicTest]);

  const handleInputChange = useCallback(
    (deviceId: string) => {
      setSelectedInput(deviceId);
      if (isTestingMic) {
        stopMicTest();
      }
    },
    [isTestingMic, setSelectedInput, stopMicTest]
  );

  const playTestSound = useCallback(async () => {
    try {
      await playSpeakerTestTone();
    } catch (error) {
      logger.error('[MicSettings] Error playing test sound:', error);
    }
  }, []);

  return { handleSave, handleMicTestToggle, handleInputChange, playTestSound };
}
